// ts-rest contract for scenario mode (internal-only, never in production).
//
// The two doors the septima-test engine drives, plus the fixture registry shape.
// The server mounts this surface unconditionally but gates every handler to 404
// unless the process booted with SCENARIO_MODE=true against a scenario database
// AND the request arrives from loopback — see
// apps/server/src/lib/runtime/scenario-mode.ts.
//
// The corpus itself is no longer served over HTTP. A scenario is a YAML flow
// under `scenarios/`, read by the septima-test engine from the checkout, so the
// state / create / update / delete routes that fed the old Scenario Centre are
// gone with it.
//
// Endpoints under /api/scenario:
//   POST   /api/scenario/clock             — advance the scenario clock
//   POST   /api/scenario/reset             — truncate domain tables + reseed the world
//   GET    /api/scenario/fixtures/:name    — the fixture bytes (plain Nest route)
//   POST   /api/scenario/client-application — one submitted online application
//   GET    /api/scenario/client-application/credential?name= — the portal sign-in address of that applicant
//   POST   /api/scenario/client-application/identity-verdict — the sandbox vendor gives its verdict (SEP-278)
//   POST   /api/scenario/fact-find/answers — the client answers each mandatory question of the fact find (SEP-293)

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodyWithCode as errorBody } from '../schemas/common';
import { clientApplicationServiceTypeSchema } from './client-application';

const c = initContract();

/** The client of one name answers each mandatory question of the open fact find. */
export const scenarioFactFindAnswersRequestSchema = z.object({
    client: z.string().trim().min(1),
});
export const scenarioFactFindAnswersResponseSchema = z.object({
    ok: z.literal(true),
    factFindId: z.string(),
    answered: z.number().int(),
});

/**
 * The client signs the signature pack on paper (SEP-318, decisions 4 and 6).
 * The door stores a signed copy of the mandate and of the client declaration.
 */
export const scenarioSignaturePackRequestSchema = z.object({
    client: z.string().trim().min(1),
});
export const scenarioSignaturePackResponseSchema = z.object({
    ok: z.literal(true),
    caseId: z.string(),
});

// ---------------------------------------------------------------------------
// Data shapes
// ---------------------------------------------------------------------------

export const scenarioFixtureInfoSchema = z.object({
    name: z.string(),
    label: z.string(),
    fileName: z.string(),
    /** The real ingestion endpoint the runner posts the file to (multipart). */
    endpoint: z.string(),
    /** Multipart field name the endpoint expects. */
    field: z.string(),
});
export type ScenarioFixtureInfo = z.infer<typeof scenarioFixtureInfoSchema>;

export const advanceScenarioClockRequestSchema = z.object({
    advanceDays: z.number().int().min(1).max(3650),
});
export type AdvanceScenarioClockRequest = z.infer<typeof advanceScenarioClockRequestSchema>;

export const advanceScenarioClockResponseSchema = z.object({
    ok: z.literal(true),
    clockOffsetDays: z.number(),
});
export type AdvanceScenarioClockResponse = z.infer<typeof advanceScenarioClockResponseSchema>;

export const resetScenarioWorldResponseSchema = z.object({
    ok: z.literal(true),
    seeded: z.object({
        users: z.number(),
        clients: z.number(),
        calls: z.number(),
    }),
});
export type ResetScenarioWorldResponse = z.infer<typeof resetScenarioWorldResponseSchema>;

/**
 * One online client application, made the way a visitor makes it (SEP-263).
 *
 * The engine reaches a page by its key in the `pages:` block of
 * `scenarios/project.yaml`, and that block holds the staff sidebar alone. The
 * public `/apply` journey is not there, so a flow about what the investment
 * manager receives cannot make the thing they receive. This door makes it,
 * through the same services the public journey calls.
 */
export const scenarioClientApplicationRequestSchema = z.object({
    forenames: z.string().min(1).max(120),
    surname: z.string().min(1).max(120),
    /** The manager of the public dropdown, by the name the directory holds. */
    investmentManagerName: z.string().min(1).max(160),
    serviceType: clientApplicationServiceTypeSchema.default('discretionary'),
    /**
     * How the identity check stands at the submission (SEP-278).
     * `complete`: the sandbox check returns Complete. `none`: the deployment
     * holds no identity provider. `unverified`: a provider is configured and
     * the applicant did not finish the check. The submission goes on, and the
     * server starts a sandbox check that waits for a verdict.
     */
    identityCheck: z.enum(['complete', 'none', 'unverified']).default('complete'),
    /**
     * The account types the applicant chooses, by the option labels of the
     * form (SEP-280). Absent: the door takes the first option of the bank.
     */
    accountTypes: z.array(z.string().min(1).max(80)).min(1).max(7).optional(),
    /**
     * A nominated representative of Part 10 (SEP-293.8). Present: the
     * applicant answers Yes and names the representative, and the door fills
     * each other required field of the block. Absent: the applicant answers
     * No.
     */
    nominatedRepresentative: z
        .object({
            forenames: z.string().min(1).max(120),
            surname: z.string().min(1).max(120),
        })
        .optional(),
    /**
     * The name of the company, when the account type is Company (SEP-293).
     * The applicant is then the nominated representative of the company, and
     * the representative is also the first director. Absent: the door names
     * the company after the applicant.
     */
    companyName: z.string().min(1).max(160).optional(),
    /**
     * The name of the account holder of another entity form (SEP-293): the
     * trust when the account type is Trust / Charity, the scheme when it is
     * SSAS. The applicant is then the nominated representative of the trust
     * (and its first trustee) or the lead individual of the scheme. Absent:
     * the door names the trust or the scheme after the applicant.
     */
    entityName: z.string().min(1).max(160).optional(),
    /**
     * One more person of an entity form (SEP-293): the second director of a
     * company, the second connected person of a trust, or the first other
     * member of an SSAS. The person gets their own identity check.
     */
    otherPerson: z
        .object({
            forenames: z.string().min(1).max(120),
            surname: z.string().min(1).max(120),
        })
        .optional(),
});
export type ScenarioClientApplicationRequest = z.infer<
    typeof scenarioClientApplicationRequestSchema
>;

export const scenarioClientApplicationResponseSchema = z.object({
    ok: z.literal(true),
    applicationId: z.string(),
    /** The address the applicant signs in with, before and after the submission. */
    email: z.string(),
    caseRef: z.string(),
    portalPath: z.string(),
    submittedAt: z.string(),
});
export type ScenarioClientApplicationResponse = z.infer<
    typeof scenarioClientApplicationResponseSchema
>;

/**
 * The portal sign-in address of one applicant who applied online.
 *
 * The door above makes the email address from the name and the instant, so a
 * flow cannot know it. The `sign-in-to-portal` command asks for it here by the
 * name that the flow wrote. The password is the fixed sandbox password of the
 * door, and this response does not carry it.
 */
export const scenarioApplicantCredentialQuerySchema = z.object({
    /** The full name, as the flow gave it to `apply-online`. */
    name: z.string().min(1).max(250),
});
export type ScenarioApplicantCredentialQuery = z.infer<
    typeof scenarioApplicantCredentialQuerySchema
>;

export const scenarioApplicantCredentialResponseSchema = z.object({
    ok: z.literal(true),
    /** The identifier that `POST /api/portal/login` accepts in its `slug` field. */
    email: z.string(),
});
export type ScenarioApplicantCredentialResponse = z.infer<
    typeof scenarioApplicantCredentialResponseSchema
>;

/**
 * The sandbox vendor gives the verdict of one identity check (SEP-278).
 *
 * The server starts the check after the submission, and a scenario cannot open
 * the vendor web app. This door plays the vendor. The server then records the
 * verdict through the same path as the webhook. `passed` writes the sandbox
 * word `fixture`, and `failed` writes `rejected`.
 */
export const scenarioIdentityVerdictRequestSchema = z.object({
    /** The full name, as the flow gave it to `apply-online`. */
    name: z.string().min(1).max(250),
    verdict: z.enum(['passed', 'failed']),
});
export type ScenarioIdentityVerdictRequest = z.infer<typeof scenarioIdentityVerdictRequestSchema>;

export const scenarioIdentityVerdictResponseSchema = z.object({
    ok: z.literal(true),
    applicationId: z.string(),
    /** The status word the sandbox wrote. */
    status: z.string(),
});
export type ScenarioIdentityVerdictResponse = z.infer<typeof scenarioIdentityVerdictResponseSchema>;

/**
 * The Feature Manifest that the scenario server runs (ADR-0027). A scenario
 * runner reads it to know which flows the deployment can run: a flow on a
 * page of a feature that is off has no page to open.
 */
export const scenarioFeaturesResponseSchema = z.object({
    /** The `client` of the manifest, for example `dowgate`. */
    client: z.string(),
    /** `sha256:<hex>` of the resolved set, as in the session. */
    manifestHash: z.string(),
    /** The enabled feature ids, sorted. */
    enabled: z.array(z.string()),
    /** True when a bought package grants every AI seat to every user. */
    grantsAllSeats: z.boolean(),
});
export type ScenarioFeaturesResponse = z.infer<typeof scenarioFeaturesResponseSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const scenarioContract = c.router(
    {
        clock: {
            method: 'POST',
            path: '/scenario/clock',
            body: advanceScenarioClockRequestSchema,
            responses: {
                200: advanceScenarioClockResponseSchema,
                404: errorBody,
            },
            summary: 'Advance the scenario clock by whole days (reset returns it to 0)',
        },
        reset: {
            method: 'POST',
            path: '/scenario/reset',
            body: z.object({}).optional(),
            responses: {
                200: resetScenarioWorldResponseSchema,
                404: errorBody,
            },
            summary: 'Truncate domain tables and reseed the scenario world',
        },
        features: {
            method: 'GET',
            path: '/scenario/features',
            responses: {
                200: scenarioFeaturesResponseSchema,
                // 404 = the scenario gate refused the request.
                404: errorBody,
            },
            summary: 'The enabled features of the Feature Manifest that the scenario server runs',
        },
        clientApplication: {
            method: 'POST',
            path: '/scenario/client-application',
            body: scenarioClientApplicationRequestSchema,
            responses: {
                200: scenarioClientApplicationResponseSchema,
                // 400 = the journey refused: an unknown manager, or a bank the
                // default answers no longer finish.
                400: errorBody,
                404: errorBody,
            },
            summary: 'Walk one online client application from the first screen to the submission',
        },
        applicantCredential: {
            method: 'GET',
            path: '/scenario/client-application/credential',
            query: scenarioApplicantCredentialQuerySchema,
            responses: {
                200: scenarioApplicantCredentialResponseSchema,
                // 404 = no submitted application holds the name, or the
                // scenario gate refused the request.
                404: errorBody,
            },
            summary: 'The portal sign-in address of the latest submitted applicant of one name',
        },
        identityVerdict: {
            method: 'POST',
            path: '/scenario/client-application/identity-verdict',
            body: scenarioIdentityVerdictRequestSchema,
            responses: {
                200: scenarioIdentityVerdictResponseSchema,
                // 400 = the application of that name holds no identity check
                // that waits for a verdict.
                400: errorBody,
                // 404 = no submitted application holds the name, or the
                // scenario gate refused the request.
                404: errorBody,
            },
            summary: 'The sandbox vendor gives the verdict of the identity check of one applicant',
        },
        factFindAnswers: {
            method: 'POST',
            path: '/scenario/fact-find/answers',
            body: scenarioFactFindAnswersRequestSchema,
            responses: {
                200: scenarioFactFindAnswersResponseSchema,
                // 400 = the fact find holds no open working version.
                400: errorBody,
                // 404 = no client or no fact find of that name, or the scenario
                // gate refused the request.
                404: errorBody,
            },
            summary: 'The client answers each mandatory question of the open fact find (SEP-293)',
        },
        signaturePack: {
            method: 'POST',
            path: '/scenario/signature-pack',
            body: scenarioSignaturePackRequestSchema,
            responses: {
                200: scenarioSignaturePackResponseSchema,
                // 404 = no client or no open case of that name, or the scenario
                // gate refused the request.
                404: errorBody,
            },
            summary: 'The client signs the mandate and the client declaration on paper (SEP-318)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
